<script setup>
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  persona: { type: Object, required: true },
  accountId: { type: [Number, String], required: true },
});
const emit = defineEmits(['duplicate', 'delete']);
const { t } = useI18n();
const router = useRouter();

const TONE_PALETTE = {
  scholarly: { bg: '#1B2A4A', role: '#C9A84C' },
  thoughtful: { bg: '#1B2A4A', role: '#C9A84C' },
  professional: { bg: '#1B2A4A', role: '#C9A84C' },
  empathetic: { bg: '#4A7AB5', role: '#3B82F6' },
  gentle: { bg: '#4A7AB5', role: '#3B82F6' },
  bold: { bg: '#C9A84C', role: '#C9A84C' },
  enthusiastic: { bg: '#C9A84C', role: '#C9A84C' },
  friendly: { bg: '#5CB85C', role: '#10B981' },
  warm: { bg: '#16A085', role: '#C9A84C' },
  casual: { bg: '#16A085', role: '#C9A84C' },
};

const palette = computed(
  () => TONE_PALETTE[props.persona.tone] || { bg: '#1B2A4A', role: '#C9A84C' }
);

const initials = computed(() => {
  const parts = (props.persona.name || '').trim().split(/\s+/);
  return parts.length >= 2
    ? (parts[0][0] + parts[1][0]).toUpperCase()
    : (parts[0]?.[0] || '?').toUpperCase();
});

const languages = computed(() => {
  const langs = props.persona.supported_languages || [];
  return langs.slice(0, 3).map(l => l.toUpperCase());
});

const roleLabel = computed(
  () => props.persona.ministry_role || props.persona.tone || ''
);

function goToEdit() {
  router.push({
    name: 'bot_personas_edit',
    params: { accountId: props.accountId, personaId: props.persona.id },
  });
}
</script>

<template>
  <div
    class="bg-white rounded-xl border border-[#E8E6E0] p-5 flex flex-col gap-3 hover:shadow-md transition-shadow cursor-default shadow-sm"
  >
    <!-- Avatar row + Live badge -->
    <div class="flex items-start justify-between">
      <div
        class="size-[52px] rounded-[14px] flex items-center justify-center text-white text-lg font-bold flex-shrink-0 overflow-hidden"
        :style="{ background: palette.bg }"
      >
        <img
          v-if="persona.avatar_url"
          :src="persona.avatar_url"
          :alt="persona.name"
          class="size-full object-cover"
        />
        <span v-else>{{ initials }}</span>
      </div>

      <span
        v-if="persona.active"
        class="flex items-center gap-1 bg-[#ECFDF5] border border-[#A7F3D0] text-[#065F46] text-[11px] font-semibold px-2 py-0.5 rounded-full"
      >
        <span class="size-1.5 rounded-full bg-[#10B981] inline-block" />
        {{ t('MINISTRY.BOT_PERSONAS.LIVE') }}
      </span>
    </div>

    <!-- Name + role + tagline -->
    <div class="flex flex-col gap-0.5">
      <p class="text-[18px] font-bold text-[#1A1A2E] leading-tight">
        {{ persona.name }}
      </p>
      <p
        v-if="roleLabel"
        class="text-[13px] font-semibold"
        :style="{ color: palette.role }"
      >
        {{ roleLabel }}
      </p>
      <p
        v-if="persona.tagline"
        class="text-[12px] text-slate-400 italic mt-0.5 line-clamp-1"
      >
        {{ persona.tagline }}
      </p>
    </div>

    <!-- Language pills -->
    <div v-if="languages.length" class="flex gap-1 flex-wrap">
      <span
        v-for="lang in languages"
        :key="lang"
        class="text-[10px] font-semibold uppercase px-[7px] py-0.5 rounded bg-[#F5F3EE] border border-[#E8E6E0] text-[#6B7280]"
      >
        {{ lang }}
      </span>
    </div>

    <!-- Conversation count -->
    <p
      v-if="persona.daily_conversations != null"
      class="text-[12px] text-slate-400"
    >
      {{ persona.daily_conversations.toLocaleString() }}
      {{ t('MINISTRY.BOT_PERSONAS.CONVERSATIONS_TODAY') }}
    </p>

    <!-- Actions -->
    <div class="flex gap-2 mt-auto pt-1">
      <button
        class="flex-1 bg-[#1B2A4A] text-white text-[13px] font-semibold py-2 rounded-lg hover:bg-[#243660] transition-colors"
        @click="goToEdit"
      >
        {{ t('MINISTRY.BOT_PERSONAS.EDIT') }}
      </button>
      <button
        class="px-4 py-2 border border-[#E8E6E0] text-[#6B7280] text-[13px] rounded-lg hover:bg-[#F5F3EE] transition-colors"
        @click="emit('duplicate', persona)"
      >
        {{ t('MINISTRY.BOT_PERSONAS.DUPLICATE') }}
      </button>
      <button
        class="px-3 py-2 border border-[#E8E6E0] text-[#6B7280] text-[13px] rounded-lg hover:bg-[#F5F3EE] transition-colors"
        :title="t('MINISTRY.BOT_PERSONAS.MORE_OPTIONS')"
        @click="emit('delete', persona)"
      >
        <span class="i-lucide-more-horizontal size-4" />
      </button>
    </div>
  </div>
</template>
